import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
  Avatar,
  LinearProgress,
} from '@mui/material';
import { Add, Check, Close, PlayArrow, Assignment, Delete, PriorityHigh, Schedule, Person } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, employeesAPI } from '../services/api';
import Swal from 'sweetalert2';

const MotionCard = motion(Card);

const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [selectedTask, setSelectedTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assigned_to: '',
    deadline: '',
    priority: 'medium',
  });
  const [completionNotes, setCompletionNotes] = useState('');
  const [stats, setStats] = useState({});

  const isAdmin = user?.role === 'admin' || user?.role === 'hr';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [tasksRes, statsRes] = await Promise.all([
        tasksAPI.getAll(),
        tasksAPI.getStats(),
      ]);
      
      if (tasksRes.data.success) {
        setTasks(tasksRes.data.data);
      }
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }
      
      if (isAdmin) {
        try {
          const empRes = await employeesAPI.getAll({ limit: 100, status: 'active' });
          if (empRes.data.success) {
            const empList = empRes.data.data.employees || empRes.data.data;
            setEmployees(empList);
          }
        } catch (err) {
          console.error('Error loading employees:', err);
        }
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title || !formData.assigned_to) {
      setMessage({ type: 'error', text: 'Please fill all required fields' });
      return;
    }
    try {
      await tasksAPI.create(formData);
      setMessage({ type: 'success', text: 'Task assigned successfully!' });
      setShowDialog(false);
      setFormData({ title: '', description: '', assigned_to: '', deadline: '', priority: 'medium' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to assign task' });
    }
  };

  const handleAccept = async (taskId) => {
    try {
      await tasksAPI.accept(taskId);
      setMessage({ type: 'success', text: 'Task accepted!' });
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to accept task' });
    }
  };

  const handleReject = async (taskId) => {
    const result = await Swal.fire({
      title: 'Enter rejection reason',
      input: 'text',
      inputPlaceholder: 'Rejection reason',
      showCancelButton: true,
      confirmButtonText: 'Submit',
      cancelButtonText: 'Cancel',
      background: '#0F2744',
      color: '#D4AF37',
      confirmButtonColor: '#D4AF37',
      cancelButtonColor: '#EF5350',
    });
    if (result.isConfirmed && result.value) {
      try {
        await tasksAPI.reject(taskId, result.value);
        setMessage({ type: 'success', text: 'Task rejected' });
        loadData();
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to reject task' });
      }
    }
  };

  const handleComplete = async () => {
    try {
      await tasksAPI.complete(selectedTask.id, completionNotes);
      setMessage({ type: 'success', text: 'Task completed!' });
      setShowCompleteDialog(false);
      setSelectedTask(null);
      setCompletionNotes('');
      loadData();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to complete task' });
    }
  };

  const handleDelete = async (taskId) => {
    const result = await Swal.fire({
      title: 'Delete Task',
      text: 'Are you sure you want to delete this task? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#EF5350',
      background: '#0F2744',
      color: '#D4AF37',
      cancelButtonColor: '#666',
    });
    if (result.isConfirmed) {
      try {
        await tasksAPI.delete(taskId);
        setMessage({ type: 'success', text: 'Task deleted' });
        loadData();
      } catch (error) {
        setMessage({ type: 'error', text: 'Failed to delete task' });
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return { bgcolor: 'rgba(255, 152, 0, 0.15)', color: '#FFB74D', border: '1px solid #FFB74D' };
      case 'accepted': return { bgcolor: 'rgba(66, 165, 245, 0.15)', color: '#64B5F6', border: '1px solid #64B5F6' };
      case 'in_progress': return { bgcolor: 'rgba(66, 165, 245, 0.15)', color: '#64B5F6', border: '1px solid #64B5F6' };
      case 'completed': return { bgcolor: 'rgba(76, 175, 80, 0.15)', color: '#66BB6A', border: '1px solid #66BB6A' };
      case 'rejected': return { bgcolor: 'rgba(239, 83, 80, 0.15)', color: '#EF5350', border: '1px solid #EF5350' };
      default: return { bgcolor: 'rgba(100, 100, 100, 0.15)', color: '#B0BEC5', border: '1px solid #B0BEC5' };
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return { bgcolor: '#EF5350', color: '#FFFFFF' };
      case 'high': return { bgcolor: '#FF9800', color: '#FFFFFF' };
      case 'medium': return { bgcolor: '#2196F3', color: '#FFFFFF' };
      case 'low': return { bgcolor: '#78909C', color: '#FFFFFF' };
      default: return { bgcolor: '#78909C', color: '#FFFFFF' };
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'urgent': return <PriorityHigh sx={{ fontSize: 16 }} />;
      case 'high': return <PriorityHigh sx={{ fontSize: 16 }} />;
      default: return <Assignment sx={{ fontSize: 16 }} />;
    }
  };

  const filterTasks = (status) => {
    if (status === 'all') return tasks;
    if (status === 'pending') return tasks.filter(t => t.status === 'pending');
    if (status === 'active') return tasks.filter(t => ['accepted', 'in_progress'].includes(t.status));
    if (status === 'completed') return tasks.filter(t => t.status === 'completed');
    return tasks;
  };

  const filteredTasks = tabValue === 0 ? filterTasks('pending') :
                       tabValue === 1 ? filterTasks('active') :
                       filterTasks('completed');

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleString();
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #0A1929 0%, #0F2744 100%)', 
      minHeight: '100vh', 
      p: { xs: 2, sm: 3 } 
    }}>
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#D4AF37', letterSpacing: 0.5 }}>
              Task Management
            </Typography>
            <Typography variant="body2" sx={{ color: '#8AA87D', mt: 0.5 }}>
              Assign, track, and manage team tasks
            </Typography>
          </Box>
          {isAdmin && (
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<Add />} 
                onClick={() => setShowDialog(true)}
                sx={{ fontWeight: 700 }}
              >
                Assign Task
              </Button>
            </motion.div>
          )}
        </Box>

        {message.text && (
          <Alert 
            severity={message.type} 
            onClose={() => setMessage({ type: '', text: '' })} 
            sx={{ mb: 3, borderRadius: 3 }}
          >
            {message.text}
          </Alert>
        )}

        {/* Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Pending Tasks', value: stats.pending || 0, color: '#FFB74D' },
            { label: 'In Progress', value: stats.inProgress || 0, color: '#64B5F6' },
            { label: 'Completed', value: stats.completed || 0, color: '#66BB6A' },
            { label: 'Rejected', value: stats.rejected || 0, color: '#EF5350' },
          ].map((stat, idx) => (
            <Grid item xs={6} sm={3} key={idx}>
              <MotionCard
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <CardContent sx={{ textAlign: 'center', py: 2.5 }}>
                  <motion.div
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, delay: idx * 0.05 + 0.1 }}
                  >
                    <Typography variant="h3" sx={{ fontWeight: 800, color: stat.color, lineHeight: 1.1 }}>
                      {stat.value}
                    </Typography>
                  </motion.div>
                  <Typography variant="body2" sx={{ color: '#8AA87D', mt: 0.5, fontWeight: 500 }}>
                    {stat.label}
                  </Typography>
                </CardContent>
              </MotionCard>
            </Grid>
          ))}
        </Grid>

        {/* Tabs */}
        <MotionCard
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          sx={{ mb: 3 }}
        >
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, v) => setTabValue(v)} 
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': { color: '#8AA87D', fontWeight: 600, textTransform: 'none' },
                '& .Mui-selected': { color: '#D4AF37' },
                '& .MuiTabs-indicator': { bgcolor: '#D4AF37', height: 3, borderRadius: '3px 3px 0 0' },
              }}
            >
              <Tab label={`Pending (${filterTasks('pending').length})`} />
              <Tab label={`In Progress (${filterTasks('active').length})`} />
              <Tab label={`Completed (${filterTasks('completed').length})`} />
            </Tabs>
          </CardContent>
        </MotionCard>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center', p: 6 }}>
            <CircularProgress size={50} sx={{ color: '#D4AF37', mb: 2 }} />
            <Typography sx={{ color: '#8AA87D', fontWeight: 500, letterSpacing: 1 }}>LOADING TASKS...</Typography>
          </Box>
        ) : (
          <MotionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <TableContainer component={Paper} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell>Assigned To</TableCell>
                      <TableCell>Deadline</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Times</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTasks.map((task) => (
                      <TableRow key={task.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Assignment sx={{ color: '#D4AF37', fontSize: 20 }} />
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF' }}>{task.title}</Typography>
                              <Typography variant="caption" sx={{ color: '#8AA87D', display: 'block', mt: 0.3 }}>
                                {task.description?.substring(0, 60)}{task.description?.length > 60 ? '...' : ''}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, bgcolor: 'rgba(212,175,55,0.15)', color: '#D4AF37', fontSize: '0.75rem', fontWeight: 700 }}>
                              {task.assignee?.first_name?.[0]}{task.assignee?.last_name?.[0]}
                            </Avatar>
                            <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                              {task.assignee?.first_name} {task.assignee?.last_name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Schedule sx={{ color: '#D4AF37', fontSize: 14 }} />
                            <Typography variant="body2" sx={{ color: '#B0BEC5' }}>
                              {task.deadline ? new Date(task.deadline).toLocaleDateString() : '-'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.priority} 
                            size="small" 
                            icon={getPriorityIcon(task.priority)}
                            sx={{ ...getPriorityColor(task.priority), fontWeight: 600, height: 26 }} 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={task.status.replace('_', ' ')} 
                            size="small" 
                            sx={{ ...getStatusColor(task.status), fontWeight: 600 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: '#8AA87D', display: 'block', fontSize: '0.65rem' }}>
                            Assigned: {formatDate(task.assigned_at)}
                          </Typography>
                          {task.accepted_at && (
                            <Typography variant="caption" sx={{ color: '#64B5F6', display: 'block', fontSize: '0.65rem' }}>
                              Accepted: {formatDate(task.accepted_at)}
                            </Typography>
                          )}
                          {task.completed_at && (
                            <Typography variant="caption" sx={{ color: '#66BB6A', display: 'block', fontSize: '0.65rem' }}>
                              Completed: {formatDate(task.completed_at)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            {!isAdmin && task.status === 'pending' && (
                              <>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleAccept(task.id)} 
                                  title="Accept Task"
                                  sx={{ color: '#66BB6A', '&:hover': { bgcolor: 'rgba(102, 187, 106, 0.1)' } }}
                                >
                                  <Check />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleReject(task.id)} 
                                  title="Reject Task"
                                  sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                                >
                                  <Close />
                                </IconButton>
                              </>
                            )}
                            {!isAdmin && task.status === 'accepted' && (
                              <Button 
                                size="small" 
                                variant="contained" 
                                onClick={() => { setSelectedTask(task); setShowCompleteDialog(true); }}
                                sx={{ 
                                  fontWeight: 700, fontSize: '0.7rem',
                                  background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
                                  '&:hover': { background: 'linear-gradient(135deg, #81C784 0%, #66BB6A 100%)' },
                                }}
                              >
                                Complete
                              </Button>
                            )}
                            {isAdmin && (
                              <IconButton 
                                size="small" 
                                onClick={() => handleDelete(task.id)} 
                                title="Delete Task"
                                sx={{ color: '#EF5350', '&:hover': { bgcolor: 'rgba(239, 83, 80, 0.1)' } }}
                              >
                                <Delete />
                              </IconButton>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredTasks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#8AA87D' }}>
                          No tasks found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </MotionCard>
        )}

        {/* Assign Task Dialog */}
        <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700, borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
            Assign New Task
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2.5} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  label="Task Title" 
                  value={formData.title} 
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  fullWidth 
                  multiline 
                  rows={3} 
                  label="Description" 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select 
                    value={formData.assigned_to} 
                    label="Assign To" 
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                  >
                    {employees.map(emp => (
                      <MenuItem key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField 
                  fullWidth 
                  type="datetime-local" 
                  label="Deadline" 
                  InputLabelProps={{ shrink: true }}
                  value={formData.deadline} 
                  onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Priority</InputLabel>
                  <Select 
                    value={formData.priority} 
                    label="Priority" 
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
            <Button onClick={() => setShowDialog(false)} sx={{ color: '#B0BEC5', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSave}
              sx={{ fontWeight: 700 }}
            >
              Assign Task
            </Button>
          </DialogActions>
        </Dialog>

        {/* Complete Task Dialog */}
        <Dialog open={showCompleteDialog} onClose={() => setShowCompleteDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ color: '#D4AF37', fontWeight: 700, borderBottom: '1px solid rgba(212,175,55,0.1)' }}>
            Complete Task
          </DialogTitle>
          <DialogContent>
            <TextField 
              fullWidth 
              multiline 
              rows={3} 
              label="Completion Notes (optional)" 
              value={completionNotes} 
              onChange={(e) => setCompletionNotes(e.target.value)} 
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(212,175,55,0.1)' }}>
            <Button onClick={() => setShowCompleteDialog(false)} sx={{ color: '#B0BEC5', fontWeight: 600 }}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleComplete}
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #66BB6A 0%, #4CAF50 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #81C784 0%, #66BB6A 100%)' },
              }}
            >
              Mark Complete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default Tasks;